/* eslint-disable react-hooks/set-state-in-effect, react/no-unescaped-entities --
 * v77.99.77: This file uses idiomatic React patterns (effect-driven
 * state hydration after fetches, English text with apostrophes /
 * quotes in JSX). The experimental React 19 hooks rules
 * (`set-state-in-effect`, `purity`, `preserve-manual-memoization`)
 * are not shipped by eslint-plugin-react-hooks 5.2.0 — only the
 * standalone Emergent lint tool surfaces them, and they mis-flag
 * these intentional patterns. `react/no-unescaped-entities` similarly
 * flags safe English text.
 */
// v77.49 — Third-party (mediator/solicitor/professional) Dashboard.
//
// Replaces the broken single-grant mediator login flow with a proper
// multi-client dashboard. After /auth/login, any user with role
// "mediator" is redirected here. They see one card per active grant
// (i.e. one per family that has shared access with them), with:
//   • Client label (granter's family) + child colour-dots and names
//   • Status badge (Active / Expiring soon / Expired / Revoked)
//   • "New" flag for grants they haven't viewed yet
//   • Permissions summary
//   • Date access was granted + expiry
//   • "View shared information" button that activates the grant
//     context (rewrites users.household_id) and lands them on the
//     usual app dashboard, scoped to that family's children.
//
// Empty state: helpful guidance when zero grants exist (e.g. just
// signed up but the granter revoked access before they got here).

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Sparkles, Users, Clock, ShieldCheck, AlertTriangle, ChevronRight, LogOut } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = {
  active: { label: "Active", class: "bg-[#E0F2F2] text-[#116B6E] border-[#A6D7D9]" },
  expired: { label: "Expired", class: "bg-[#FBF0DC] text-[#825A41] border-[#E8C29A]" },
  revoked: { label: "Revoked", class: "bg-[#F8DDDA] text-[#9A2A23] border-[#E8B8B2]" },
};

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  } catch { return iso; }
}

function daysUntil(iso) {
  if (!iso) return null;
  try {
    const ms = new Date(iso).getTime() - Date.now();
    return Math.ceil(ms / (1000 * 60 * 60 * 24));
  } catch { return null; }
}

function PermSummary({ perms }) {
  // Counts how many CORE permissions are on. Sensitive ones are
  // surfaced separately so the third party knows when they have
  // unusual access.
  const core = ["view_messages", "view_calendar", "view_expenses", "view_upcoming_expenses", "view_documents", "view_journal", "view_requests"];
  const sensitive = ["view_private_journal", "view_private_documents", "view_expense_details"];
  const onCore = core.filter((k) => perms[k]).length;
  const onSensitive = sensitive.filter((k) => perms[k]);
  return (
    <div className="text-xs text-[#6F828A]">
      {onCore}/{core.length} core areas
      {onSensitive.length > 0 && (
        <span className="ml-2 text-[#825A41]">· {onSensitive.length} sensitive</span>
      )}
    </div>
  );
}

export default function ThirdPartyDashboard() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [grants, setGrants] = useState(null);
  const [activating, setActivating] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/third-party/grants");
      setGrants(data.grants || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't load your client list");
      setGrants([]);
    }
  };

  useEffect(() => { load(); }, []);

  const sorted = useMemo(() => {
    if (!grants) return [];
    // Active first, then by `is_new` desc, then by accepted_at desc.
    const order = { active: 0, expiring: 1, expired: 2, revoked: 3 };
    return [...grants].sort((a, b) => {
      const da = daysUntil(a.expires_at);
      const ax = (a.status === "active" && da !== null && da <= 7) ? "expiring" : a.status;
      const db_ = daysUntil(b.expires_at);
      const bx = (b.status === "active" && db_ !== null && db_ <= 7) ? "expiring" : b.status;
      if (order[ax] !== order[bx]) return (order[ax] ?? 9) - (order[bx] ?? 9);
      if ((b.is_new ? 1 : 0) !== (a.is_new ? 1 : 0)) return (b.is_new ? 1 : 0) - (a.is_new ? 1 : 0);
      return (b.accepted_at || "").localeCompare(a.accepted_at || "");
    });
  }, [grants]);

  const newCount = useMemo(() => (grants || []).filter((g) => g.is_new).length, [grants]);

  const activate = async (g) => {
    if (g.status !== "active") {
      toast.error("This client's access is no longer active.");
      return;
    }
    setActivating(g.id);
    try {
      await api.post(`/third-party/grants/${g.id}/activate`);
      // v77.99.68 — Refresh the user payload so `active_client_label`
      // and the new household_id are reflected in the topbar pill,
      // greeting, and sidebar before we navigate.
      await refresh();
      toast.success(`Switched to ${g.client_label}`);
      navigate("/app/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't switch client");
    } finally {
      setActivating(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#F2F4F4]" data-testid="third-party-dashboard">
      <header className="bg-white border-b border-[#D9E1E3] px-4 sm:px-6 py-4 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.12em] text-[#6F828A] font-medium">CoParent Console · Third-party access</div>
          <div className="font-heading text-xl text-[#1A1F22]">{user?.name || "Welcome"}</div>
        </div>
        <Button variant="ghost" onClick={logout} data-testid="third-party-logout">
          <LogOut className="w-4 h-4 mr-1.5" />Sign out
        </Button>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        <div className="space-y-1">
          <h1 className="font-heading text-2xl font-medium text-[#1A1F22]">Your clients</h1>
          <p className="text-sm text-[#3F595B]">
            Each card below is a family that has granted you access to specific records. Tap “View shared information” to enter that client's view. You'll only see what's been explicitly shared with you.
          </p>
        </div>

        {newCount > 0 && (
          <div className="bg-[#E0F2F2] border border-[#A6D7D9] rounded-md px-4 py-3 text-sm text-[#116B6E] flex items-center gap-2" data-testid="third-party-new-flag">
            <Sparkles className="w-4 h-4 flex-shrink-0" strokeWidth={1.6} />
            <span><strong>{newCount}</strong> new {newCount === 1 ? "client has" : "clients have"} shared access with you. Cards marked “New” below haven't been opened yet.</span>
          </div>
        )}

        {grants === null && (
          <div className="text-sm text-[#6F828A]" data-testid="third-party-loading">Loading your clients…</div>
        )}

        {grants !== null && sorted.length === 0 && (
          <Card className="border-[#D9E1E3] bg-white" data-testid="third-party-empty">
            <CardContent className="p-6 text-center space-y-3">
              <div className="font-medium text-[#1A1F22]">No active client access</div>
              <p className="text-sm text-[#6F828A]">
                You don't currently have access to any family's records. If a parent has invited you, they'll need to share an active invite link or code. If your access has expired or been revoked, please contact the parent directly.
              </p>
            </CardContent>
          </Card>
        )}

        {grants !== null && sorted.map((g) => {
          const days = daysUntil(g.expires_at);
          const expiringSoon = g.status === "active" && days !== null && days <= 7;
          const tone = STATUS_LABEL[g.status] || STATUS_LABEL.active;
          return (
            <Card key={g.id} className="border-[#D9E1E3] bg-white" data-testid={`third-party-grant-${g.id}`}>
              <CardHeader className="border-b border-[#D9E1E3]">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <CardTitle className="font-heading text-lg flex items-center gap-2 flex-wrap">
                      <span className="truncate">{g.client_label}</span>
                      {g.is_new && (
                        <span className="text-[10px] uppercase tracking-[0.08em] font-semibold text-[#116B6E] bg-[#D6F0EF] border border-[#A6D7D9] rounded-sm px-1.5 py-0.5" data-testid={`third-party-new-${g.id}`}>
                          New
                        </span>
                      )}
                      <span className={`text-[10px] uppercase tracking-[0.08em] font-semibold rounded-sm px-1.5 py-0.5 border ${tone.class}`} data-testid={`third-party-status-${g.id}`}>
                        {tone.label}
                      </span>
                      {expiringSoon && (
                        <span className="text-[10px] uppercase tracking-[0.08em] font-semibold text-[#825A41] bg-[#FBF0DC] border border-[#E8C29A] rounded-sm px-1.5 py-0.5" data-testid={`third-party-expiring-${g.id}`}>
                          Expires in {days}d
                        </span>
                      )}
                    </CardTitle>
                    {g.granter && (
                      <div className="text-xs text-[#6F828A] mt-1">
                        Invited by <span className="text-[#2E3A3F] font-medium">{g.granter.name || "—"}</span>
                        {g.granter.email && (<> · <span className="font-mono-data">{g.granter.email}</span></>)}
                      </div>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3 pt-4">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A] font-medium mb-1">
                    Children shared with you ({g.children.length})
                  </div>
                  {g.children.length === 0 ? (
                    <div className="text-xs text-[#825A41] italic">No children granted — you'll only see household-wide info.</div>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {g.children.map((c) => (
                        <span
                          key={c.id}
                          className="inline-flex items-center gap-1.5 text-sm border border-[#D9E1E3] bg-[#FAFBFB] rounded-sm pl-2 pr-2 py-1"
                          data-testid={`third-party-child-${g.id}-${c.id}`}
                        >
                          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: c.color || "#1F9FA4" }} />
                          <span className="text-[#2E3A3F]">{c.name}</span>
                          {c.archived && (
                            <span className="text-[9px] uppercase tracking-[0.08em] text-[#6F828A]">archived</span>
                          )}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <PermSummary perms={g.permissions || {}} />

                <div className="grid grid-cols-2 gap-2 text-xs text-[#6F828A] pt-2 border-t border-[#D9E1E3]">
                  <div>
                    <span className="block text-[#9CA8AC] uppercase tracking-[0.06em] text-[10px]">Access granted</span>
                    <span className="text-[#2E3A3F] font-mono-data">{formatDate(g.accepted_at || g.created_at)}</span>
                  </div>
                  <div>
                    <span className="block text-[#9CA8AC] uppercase tracking-[0.06em] text-[10px]">Expires</span>
                    <span className="text-[#2E3A3F] font-mono-data">{formatDate(g.expires_at)}</span>
                  </div>
                </div>

                <div className="flex justify-end pt-2">
                  <Button
                    onClick={() => activate(g)}
                    disabled={g.status !== "active" || activating === g.id}
                    className="bg-[#116B6E] hover:bg-[#0F5658] text-white"
                    data-testid={`third-party-view-${g.id}`}
                  >
                    {activating === g.id ? "Switching…" : "View shared information"}
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </main>
    </div>
  );
}
